import React, { useRef, useState, useEffect } from 'react';
import jsQR from 'jsqr';
import { saveScanToHistory } from '../../utils/storage';
import './QRScanner.css';

const QRScanner = ({ onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setScanning(true);
      setError(null);
      
      // Start scanning for QR codes
      scanQRCode();
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Could not access camera. Please ensure you have granted camera permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setScanning(false);
  };

  const scanQRCode = () => {
    if (!videoRef.current || videoRef.current.readyState !== 4) {
      if (scanning) {
        requestAnimationFrame(scanQRCode);
      }
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert',
    });

    if (code) {
      handleScanSuccess(code.data);
    } else {
      // Continue scanning
      if (scanning) {
        requestAnimationFrame(scanQRCode);
      }
    }
  };

  const handleScanSuccess = (data) => {
    setScanResult(data);
    stopCamera();
    
    // Save to history
    saveScanToHistory(data);
    
    // Open the scanned URL in a new tab
    setTimeout(() => {
      window.open(data, '_blank');
      handleClose();
    }, 1500);
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-container">
        <div className="scanner-header">
          <h2>Scan QR Code</h2>
          <button className="close-button" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="scanner-content">
          {error ? (
            <div className="error-message">
              <p>{error}</p>
              <button className="retry-button" onClick={startCamera}>
                Retry Camera Access
              </button>
            </div>
          ) : (
            <>
              <div className="video-container">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted
                  className="scanner-video"
                  onPlay={() => scanning && scanQRCode()}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div className="scan-overlay">
                  <div className="scan-frame"></div>
                </div>
              </div>
              
              {scanResult && (
                <div className="scan-result">
                  <p>Scanned successfully! Opening link...</p>
                </div>
              )}
              
              {!scanResult && (
                <div className="scanner-info">
                  <p>Position QR code within frame to scan</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;